// "use client";

import React from "react";
import { Typography, theme } from "antd";
import { CustomButton } from "../atoms";

const { Title, Paragraph } = Typography;

type Size = "sm" | "md" | "lg";

export type NotFoundCardProps = {
    /** Большой заголовок (например, 404) */
    code?: React.ReactNode;
    /** Подзаголовок под кодом */
    subtitle?: React.ReactNode;
    /** Текст на кнопке */
    actionText?: string;
    /** Обработчик клика по кнопке */
    onAction?: () => void;
    /** Если указать href — кнопка станет ссылкой */
    href?: string;
    /** Визуальный размер карточки */
    size?: Size;
    /** Проброс стилей/класса */
    style?: React.CSSProperties;
    className?: string;
    /** Доп. контент под подзаголовком (опционально) */
    children?: React.ReactNode;
};

const SIZE_MAP: Record<Size, { pad: string; font: string; maxW: number }> = {
    sm: {
        pad: "clamp(16px, 2.5vw, 28px)",
        font: "clamp(64px, 12vw, 120px)",
        maxW: 720,
    },
    md: {
        pad: "clamp(24px, 4vw, 48px)",
        font: "clamp(120px, 20vw, 260px)",
        maxW: 920,
    },
    lg: {
        pad: "clamp(32px, 5vw, 64px)",
        font: "clamp(160px, 24vw, 320px)",
        maxW: 1080,
    },
};

export default function NotFoundCard({
    code = "404",
    subtitle = "Страница не найдена",
    actionText = "На главную",
    onAction,
    href,
    size = "md",
    style,
    className,
    children,
}: NotFoundCardProps) {
    const { token } = theme.useToken();
    const s = SIZE_MAP[size];

    return (
        <div
            role="region"
            aria-label="Страница не найдена"
            className={className}
            style={{
                width: "min(100%, " + s.maxW + "px)",
                textAlign: "center",
                padding: s.pad,
                borderRadius: 24,
                background: token.colorBgContainer,
                border: `1px solid ${token.colorBorderSecondary}`,
                boxShadow: token.boxShadowSecondary,
                ...style,
            }}
        >
            <Title
                level={1}
                style={{
                    margin: 0,
                    fontWeight: 900,
                    lineHeight: 1,
                    letterSpacing: "-0.02em",
                    fontSize: s.font,
                    background: `linear-gradient(135deg, ${token.colorTextTertiary} 0%, ${token.colorText} 55%, ${token.colorPrimary} 100%)`,
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    textShadow: `0 6px 24px ${token.colorFillSecondary}`,
                    userSelect: "none",
                }}
            >
                {code}
            </Title>

            <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 20, fontSize: 18 }}>
                {subtitle}
            </Paragraph>

            {children ? <div style={{ marginBottom: 16 }}>{children}</div> : null}

            <CustomButton
                customVariant="primary"
                size="lg"
                onClick={href ? undefined : onAction}
                href={href}
            >
                {actionText}
            </CustomButton>
        </div>
    );
}
